import { MessagesSquare } from 'lucide-react';
import React from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/UserAvatar';
import { useStore } from '@/store';
import { getRelativeTime } from '@/utils';

import type { FeedbackReviewFeedItem } from '../qcFeedbackReviews.utils';
import { QcFeedbackReviewsCountBadge } from './QcFeedbackReviewsCountBadge';

interface QcFeedbackReviewsFeedProps {
  feedbackError?: boolean;
  feedbackLoading?: boolean;
  headerAction?: React.ReactNode;
  items: FeedbackReviewFeedItem[];
  title?: string;
  userNameById?: Map<string, string>;
}

const FeedbackReviewsLoadingSkeleton: React.FC = () => (
  <div className="space-y-3.5">
    {[1, 2, 3].map(i => (
      <div key={i} className="flex items-start gap-2">
        <Skeleton className="mt-0.5 size-6 rounded-full" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
    ))}
  </div>
);

const resolveActorName = (userId?: string, userNameById?: Map<string, string>) => {
  if (!userId) {
    return 'QC Reviewer';
  }

  return userNameById?.get(userId) || 'Unknown Reviewer';
};

const FeedbackReviewRow: React.FC<{
  item: FeedbackReviewFeedItem;
  timeZone: ReturnType<typeof useStore.getState>['timeZone'];
  userNameById?: Map<string, string>;
}> = ({ item, timeZone, userNameById }) => {
  const actorName = resolveActorName(item.actorUserId, userNameById);

  return (
    <div className="flex items-start gap-2">
      <UserAvatar
        name={actorName}
        userId={item.actorUserId}
        size="sm"
        className="mt-0.5 shrink-0"
      />

      <div className="min-w-0 flex-1">
        <p className="text-[13px] leading-5">
          <span className="font-semibold text-foreground">{actorName}</span>
          <span className="ml-1 text-muted-foreground">on {item.contextLabel}</span>
        </p>

        <div className="mt-1 rounded-xl bg-muted/35 px-2.5 py-1.5">
          <p className="break-words text-[13px] leading-5 text-foreground">{item.message}</p>
        </div>

        <p className="mt-0.5 text-[10px] leading-4 text-muted-foreground">
          {getRelativeTime(item.occurredAt, timeZone)}
        </p>
      </div>
    </div>
  );
};

export const QcFeedbackReviewsFeed: React.FC<QcFeedbackReviewsFeedProps> = ({
  feedbackError = false,
  feedbackLoading = false,
  headerAction,
  items,
  title = 'Feedbacks',
  userNameById,
}) => {
  const timeZone = useStore(state => state.timeZone);

  return (
    <div className="flex h-full flex-col">
      <div className="border-b bg-gradient-to-b from-muted/35 to-background px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-2 items-center min-w-0">
            <h3 className="font-semibold text-sm">{title}</h3>
            <QcFeedbackReviewsCountBadge count={items.length} />
          </div>
          {headerAction}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-background px-4 py-3.5">
        {feedbackLoading && items.length === 0 ? (
          <FeedbackReviewsLoadingSkeleton />
        ) : feedbackError && items.length === 0 ? (
          <div className="py-8 text-center text-destructive">
            <p className="text-sm">Failed to load feedback and reviews</p>
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <MessagesSquare className="mx-auto mb-3 h-8 w-8 opacity-50" />
            <p className="text-sm">No feedback or reviews yet</p>
          </div>
        ) : (
          <div className="space-y-3.5">
            {items.map(item => (
              <FeedbackReviewRow
                key={item.id}
                item={item}
                timeZone={timeZone}
                userNameById={userNameById}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
